import { ReactNode, createContext, useContext, useMemo, useRef } from "react";
import { GridMove, GridPosition, positionKeyOf, targetPositionOf } from "./gridNavigation";

interface RegisteredCell {
  position: GridPosition;
  element: HTMLInputElement;
}

interface GridNavigationValue {
  registerCell: (position: GridPosition, element: HTMLInputElement) => void;
  /** Removes a cell, unless another one has taken its place meanwhile. */
  unregisterCell: (position: GridPosition, element: HTMLInputElement) => void;
  /** The input a move from `from` lands on, or null when there is none. */
  cellAt: (from: GridPosition, move: GridMove) => HTMLInputElement | null;
}

const GridNavigationContext = createContext<GridNavigationValue | null>(null);

interface GridNavigationProviderProps {
  children: ReactNode;
}

/**
 * Wraps one editable grid (a table of `GridCellInput`s) so Tab / Enter move between its cells
 * like Excel. Cells register themselves by row and column while mounted; two grids on one page
 * each need their own provider.
 */
export function GridNavigationProvider({ children }: Readonly<GridNavigationProviderProps>) {
  const cells = useRef(new Map<string, RegisteredCell>());

  const value = useMemo<GridNavigationValue>(
    () => ({
      registerCell: (position, element) => {
        cells.current.set(positionKeyOf(position), { position, element });
      },
      unregisterCell: (position, element) => {
        const key = positionKeyOf(position);
        if (cells.current.get(key)?.element === element) cells.current.delete(key);
      },
      cellAt: (from, move) => {
        const registered = Array.from(cells.current.values());
        const target = targetPositionOf(
          registered.map((cell) => cell.position),
          from,
          move,
        );
        return target ? (cells.current.get(positionKeyOf(target))?.element ?? null) : null;
      },
    }),
    [],
  );

  return (
    <GridNavigationContext.Provider value={value}>{children}</GridNavigationContext.Provider>
  );
}

export const useGridNavigation = (): GridNavigationValue => {
  const navigation = useContext(GridNavigationContext);
  if (!navigation) throw new Error("GridCellInput must be rendered inside a GridNavigationProvider");
  return navigation;
};
